import { useEffect, useState } from 'react';
import { getAppConfig, updateAppConfig, setAppConfigField } from '../services/configService';
import type { AppConfig, BatteryTier } from '../types';

export default function ConfigPage() {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editField, setEditField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');

  useEffect(() => { loadConfig(); }, []);

  async function loadConfig() {
    setLoading(true);
    try { setConfig(await getAppConfig()); }
    finally { setLoading(false); }
  }

  async function saveField(field: string, value: unknown) {
    setSaving(true);
    try {
      await setAppConfigField(field, value);
      await loadConfig();
      setEditField(null);
    } finally { setSaving(false); }
  }

  function startEdit(field: string, currentValue: unknown) {
    setEditField(field);
    setEditValue(typeof currentValue === 'object' ? JSON.stringify(currentValue, null, 2) : String(currentValue));
  }

  function commitEdit(field: string) {
    let val: unknown = editValue;
    if (editValue === 'true') val = true;
    else if (editValue === 'false') val = false;
    else if (!isNaN(Number(editValue)) && editValue.trim() !== '') val = Number(editValue);
    else {
      try { val = JSON.parse(editValue); } catch { val = editValue; }
    }
    saveField(field, val);
  }

  if (loading) return <div className="text-gray-500">로딩 중...</div>;
  if (!config) return <div className="text-red-500">앱 설정을 불러올 수 없습니다.</div>;

  const sections: { title: string; fields: { key: string; label: string }[] }[] = [
    {
      title: '배터리 설정',
      fields: [
        { key: 'defaultBattery', label: '기본 배터리' },
        { key: 'signupDefaultCells', label: '가입 시 지급 셀' },
        { key: 'messageSendCost', label: '메시지 전송 비용' },
        { key: 'dmSendCost', label: 'DM 전송 비용' },
        { key: 'nicknameChangeCost', label: '닉네임 변경 비용' },
        { key: 'rewardAdAmount', label: '리워드 광고 지급량' },
        { key: 'autoChargeIntervalMinutes', label: '자동충전 간격(분)' },
        { key: 'autoChargeAmount', label: '자동충전 금액' },
        { key: 'batteryConsumptionIntervalSec', label: '감도 소모 간격(초)' },
      ],
    },
    {
      title: '감도별 비용 (batteryConsumption)',
      fields: [{ key: 'batteryConsumption', label: '감도 테이블' }],
    },
    {
      title: '수동 주파수 모드',
      fields: [
        { key: 'manualFreqMode', label: '수동 주파수 설정' },
      ],
    },
    {
      title: '위치 점프 권한',
      fields: [
        { key: 'locationJumpPermissions', label: '위치 점프 권한' },
        { key: 'mapLongPressPermissions', label: '맵 롱프레스 권한' },
      ],
    },
    {
      title: '메시지 수신',
      fields: [
        { key: 'messageReception', label: '메시지 수신 설정' },
      ],
    },
    {
      title: '알림 설정',
      fields: [
        { key: 'serverNotification', label: '서버 알림' },
        { key: 'nearbyUserNotification', label: '주변 사용자 알림' },
      ],
    },
    {
      title: '드롭 설정',
      fields: [
        { key: 'dropBaseCost', label: '드롭 기본 비용' },
        { key: 'dropCostPerHour', label: '시간당 비용' },
        { key: 'dropCostPerKm', label: 'km당 비용' },
        { key: 'dropMaxDurationHours', label: '최대 지속시간(h)' },
        { key: 'dropMaxRadiusKm', label: '최대 반경(km)' },
      ],
    },
    {
      title: 'UI 설정',
      fields: [
        { key: 'bannerPosition', label: '배너 위치' },
        { key: 'showCellPurchaseButton', label: '셀 구매 버튼' },
        { key: 'showRewardAdButton', label: '리워드 광고 버튼' },
        { key: 'nearbyUsersLimit', label: '주변 사용자 제한' },
        { key: 'radarDisplayTimeMinutes', label: '레이더 표시 시간(분)' },
        { key: 'radarValidationIntervalSeconds', label: '레이더 갱신 간격(초)' },
      ],
    },
    {
      title: '서버 설정',
      fields: [
        { key: 'appActiveThresholdSeconds', label: '활성 판단 기준(초)' },
        { key: 'maxNotifiedMessageIds', label: '최대 알림 메시지 ID 수' },
        { key: 'maxSearchRadiusMeters', label: '최대 검색 반경(m)' },
        { key: 'maxActiveUsersTimeRangeMinutes', label: '활성 사용자 시간범위(분)' },
      ],
    },
    {
      title: '앱 버전',
      fields: [
        { key: 'minAppVersion', label: '최소 앱 버전' },
      ],
    },
    {
      title: '신규 사용자 기본값',
      fields: [
        { key: 'newUserDefaults', label: '신규 사용자 기본 설정' },
      ],
    },
    {
      title: '지도 표시 동의 플로우',
      fields: [
        { key: 'mapConsentRequiredPlatforms', label: '플랫폼별 동의 필수 (true=엄격, false=완화/항상표시)' },
      ],
    },
  ];

  function renderValue(key: string, val: unknown) {
    if (editField === key) {
      const isMultiline = typeof val === 'object';
      return (
        <div className="flex gap-2 items-start">
          {isMultiline ? (
            <textarea
              value={editValue}
              onChange={e => setEditValue(e.target.value)}
              rows={6}
              className="flex-1 px-2 py-1 border border-blue-300 rounded text-sm font-mono resize-y"
            />
          ) : (
            <input
              value={editValue}
              onChange={e => setEditValue(e.target.value)}
              className="flex-1 px-2 py-1 border border-blue-300 rounded text-sm font-mono"
              onKeyDown={e => e.key === 'Enter' && commitEdit(key)}
            />
          )}
          <button onClick={() => commitEdit(key)} disabled={saving} className="px-2 py-1 text-xs bg-blue-600 text-white rounded">저장</button>
          <button onClick={() => setEditField(null)} className="px-2 py-1 text-xs text-gray-500 border border-gray-300 rounded">취소</button>
        </div>
      );
    }

    if (typeof val === 'boolean') {
      return (
        <button
          onClick={() => saveField(key, !val)}
          className={`px-2 py-0.5 text-xs rounded ${val ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
        >
          {val ? 'true' : 'false'}
        </button>
      );
    }

    if (typeof val === 'object' && val !== null) {
      return (
        <pre
          className="text-xs font-mono bg-gray-50 p-2 rounded max-h-32 overflow-y-auto cursor-pointer hover:bg-gray-100"
          onClick={() => startEdit(key, val)}
        >
          {JSON.stringify(val, null, 2)}
        </pre>
      );
    }

    return (
      <span
        className="font-mono cursor-pointer hover:text-blue-600"
        onClick={() => startEdit(key, val)}
      >
        {String(val)}
      </span>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">앱 설정</h1>
        <button onClick={loadConfig} className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50">새로고침</button>
      </div>

      <div className="space-y-6">
        {sections.map(section => (
          <div key={section.title} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="font-semibold mb-4">{section.title}</h2>
            <div className="space-y-3">
              {section.fields.map(field => {
                const val = (config as Record<string, unknown>)[field.key];
                return (
                  <div key={field.key} className="flex items-start gap-4">
                    <div className="w-48 flex-shrink-0">
                      <span className="text-sm text-gray-500">{field.label}</span>
                      <span className="block text-xs font-mono text-gray-300">{field.key}</span>
                    </div>
                    <div className="flex-1">{renderValue(field.key, val)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
